import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import CartDrawer from "@/components/CartDrawer";
import FreeShippingBanner from "@/components/FreeShippingBanner";

const NAV = [
    { label: "Roasts", to: "/#products" },
    { label: "First Responders", to: "/#wholesale" },
    { label: "Our Mission", to: "/#mission" },
    { label: "Contact", to: "/#contact" },
];

const Header = () => {
    const { totalCount, setIsOpen } = useCart();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleAnchor = (to) => {
        setMobileOpen(false);
        if (to.startsWith("/#")) {
            const id = to.slice(2);
            if (location.pathname !== "/") {
                navigate("/");
                setTimeout(() => {
                    document
                        .getElementById(id)
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 80);
            } else {
                document
                    .getElementById(id)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        } else {
            navigate(to);
        }
    };

    return (
        <>
            <div className="fixed top-0 inset-x-0 z-40">
                <FreeShippingBanner />
                <header
                    data-testid="site-header"
                    className={`transition-all duration-300 ${
                        scrolled
                            ? "bg-[#0B0E14]/95 backdrop-blur-md border-b border-[#1E293B]"
                            : "bg-transparent"
                    }`}
                >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo */}
                        <Link
                            to="/"
                            data-testid="header-logo-link"
                            className="flex items-center"
                        >
                            <img
                                src="/bs-logo-v2.png"
                                alt="Badge & Shield Coffee Co."
                                className="h-14 md:h-16 w-auto object-contain"
                                style={{
                                    filter:
                                        "drop-shadow(0 2px 6px rgba(0,0,0,0.55))",
                                }}
                            />
                        </Link>

                        {/* Desktop nav */}
                        <nav className="hidden lg:flex items-center gap-10">
                            {NAV.map((item) => (
                                <button
                                    key={item.label}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                    onClick={() => handleAnchor(item.to)}
                                    className="font-chivo text-xs tracking-[0.22em] uppercase text-slate-300 hover:text-[#ffffff] transition-colors"
                                >
                                    {item.label}
                                </button>
                            ))}
                        </nav>

                        {/* Right actions */}
                        <div className="flex items-center gap-3">
                            <button
                                data-testid="open-cart-button"
                                onClick={() => setIsOpen(true)}
                                className="relative p-3 border border-[#1E293B] hover:border-[#ffffff] transition-colors group"
                                aria-label="Open cart"
                            >
                                <ShoppingBag className="w-5 h-5 text-white" />
                                {totalCount > 0 && (
                                    <span
                                        data-testid="cart-count-badge"
                                        className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 bg-[#004466] text-white text-[11px] font-chivo font-bold flex items-center justify-center"
                                    >
                                        {totalCount}
                                    </span>
                                )}
                            </button>
                            <button
                                data-testid="mobile-menu-toggle"
                                className="lg:hidden p-3 border border-[#1E293B] text-white"
                                onClick={() => setMobileOpen((v) => !v)}
                                aria-label="Toggle menu"
                            >
                                {mobileOpen ? (
                                    <X className="w-5 h-5" />
                                ) : (
                                    <Menu className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile nav */}
                {mobileOpen && (
                    <div
                        data-testid="mobile-nav"
                        className="lg:hidden bg-[#0B0E14] border-t border-[#1E293B]"
                    >
                        <div className="max-w-[1400px] mx-auto px-6 py-6 flex flex-col gap-1">
                            {NAV.map((item) => (
                                <button
                                    key={item.label}
                                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                    onClick={() => handleAnchor(item.to)}
                                    className="text-left font-chivo text-sm tracking-[0.2em] uppercase text-slate-300 hover:text-[#ffffff] py-3 border-b border-[#1E293B] last:border-0"
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </header>
            </div>
            <CartDrawer />
        </>
    );
};

const Logo = () => (
    <svg
        viewBox="0 0 64 64"
        className="w-10 h-10"
        aria-hidden="true"
    >
        <path
            d="M32 4 L56 12 L56 32 C56 46 44 58 32 60 C20 58 8 46 8 32 L8 12 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="3"
        />
        <path
            d="M32 18 L36.5 28 L47 28 L38.5 35 L42 45 L32 39 L22 45 L25.5 35 L17 28 L27.5 28 Z"
            fill="#004466"
        />
    </svg>
);

export default Header;
